import { TYPES_TVA, ecritTaux, tauxTvaEnVigueur } from '../../lib/tva';
import { C } from '../../theme/palette';

/**
 * Choix du taux de TVA d'un article.
 *
 * <p>Un magasin vend au taux normal et au taux réduit le même jour :
 * l'alimentation, les boissons non alcoolisées, les livres et les médicaments
 * relèvent du taux réduit, l'hébergement du taux spécial (LTVA art. 25).
 * Appliquer le taux normal à tout réclame au client une taxe qu'il ne doit
 * pas, et fausse le décompte remis à l'AFC.
 *
 * <p>La valeur nulle n'est pas une absence : elle veut dire « le taux de la
 * société ». Un changement de taux légal — comme le passage de 7,7 à 8,1 % en
 * 2024 — se fait alors en un seul endroit (Réglages → Documents → TVA), sans
 * reprendre chaque article. Zéro, lui, est un taux : celui d'un article
 * exonéré.
 *
 * <p>Les taux proposés sont ceux en vigueur aujourd'hui : c'est aujourd'hui
 * que l'article se vend.
 *
 * @param {object} props propriétés du composant
 * @param {number|null} props.valeur taux retenu, nul pour celui de la société
 * @param {(taux: number|null) => void} props.onChange appelé au choix d'un taux
 * @param {object} [props.soc] société, dont on lit les taux en vigueur
 * @returns {JSX.Element} la rangée de choix
 */
export function TauxTvaArticle({ valeur, onChange, soc }) {
  const taux = tauxTvaEnVigueur(soc);
  const normal = taux.normal;

  // Le premier choix laisse la valeur nulle : c'est ce qui rattache l'article
  // au taux de la societe plutot que d'y recopier un nombre qui se perimerait.
  // Le taux reduit, puis le taux special, puis l'exoneration : l'ordre de la
  // maquette, qui est celui de la loi.
  const genre = { reduit: 'réduit', hebergement: 'spécial' };
  const choix = [
    { cle: '', libelle: `${ecritTaux(normal)} · standard`, taux: null },
    ...TYPES_TVA.filter((t) => t.cle !== 'normal' && taux[t.cle] !== normal).map((t) => ({
      cle: t.cle,
      libelle: `${ecritTaux(taux[t.cle])} · ${genre[t.cle]}`,
      taux: taux[t.cle],
    })),
    { cle: 'exonere', libelle: `${ecritTaux(0)} · exonéré`, taux: 0 },
  ];

  return (
    <div style={{ marginBottom: 11 }}>
      <div
        style={{
          fontSize: 9,
          fontWeight: 800,
          color: C.muted,
          marginBottom: 7,
          textTransform: 'uppercase',
          letterSpacing: '.3px',
        }}
      >
        💠 Taux de TVA
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {choix.map((c) => {
          // Un taux enregistre egal au taux normal — par import, ou parce que
          // la societe a change de taux — doit se lire comme « standard »,
          // faute de quoi aucune puce ne s'allume et l'utilisateur ne sait
          // pas ce qui s'applique.
          // La valeur nulle ne se compare pas en nombre : `+null` vaut 0, et
          // la puce « exonéré » s'allumait avec « standard ».
          const actif =
            c.taux === null
              ? valeur == null || +valeur === normal
              : valeur != null && +valeur === c.taux;
          return (
            <div
              key={c.cle}
              onClick={() => onChange(c.taux)}
              style={{
                padding: '8px 12px',
                borderRadius: 10,
                fontSize: 10.5,
                fontWeight: 700,
                cursor: 'pointer',
                border: '1.5px solid ' + (actif ? C.teal : C.border),
                background: actif ? C.teal : '#fff',
                color: actif ? '#fff' : C.ink,
              }}
            >
              {c.libelle}
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 8, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
        Par défaut <b>{ecritTaux(normal)}</b>. À changer seulement pour les produits à taux réduit
        (boissons non alcoolisées, alimentation, livres, médicaments).
      </div>
    </div>
  );
}
